/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type Option,
  type OptionOrNullable,
  type ReadonlyUint8Array,
} from "@solana/kit";
import {
  getArbitratorDecisionDecoder,
  getArbitratorDecisionEncoder,
  getEscrowStatusDecoder,
  getEscrowStatusEncoder,
  type ArbitratorDecision,
  type ArbitratorDecisionArgs,
  type EscrowStatus,
  type EscrowStatusArgs,
} from "../types";

export const GHOST_PROTECT_ESCROW_DISCRIMINATOR = new Uint8Array([
  38, 0, 98, 160, 102, 4, 51, 160,
]);

export function getGhostProtectEscrowDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    GHOST_PROTECT_ESCROW_DISCRIMINATOR,
  );
}

export type GhostProtectEscrow = {
  discriminator: ReadonlyUint8Array;
  /** Unique escrow ID */
  escrowId: bigint;
  /** Client (payer) */
  client: Address;
  /** Agent (service provider) */
  agent: Address;
  /** Payment amount */
  amount: bigint;
  /** Payment token mint */
  tokenMint: Address;
  /** Escrow status */
  status: EscrowStatus;
  /** Job description (IPFS hash) */
  jobDescription: string;
  /** Delivery proof (IPFS hash) */
  deliveryProof: Option<string>;
  /** Deadline timestamp */
  deadline: bigint;
  /** Created timestamp */
  createdAt: bigint;
  /** Completed/disputed timestamp */
  completedAt: Option<bigint>;
  /** Dispute reason (if disputed) */
  disputeReason: Option<string>;
  /** Arbitrator decision (if disputed) */
  arbitratorDecision: Option<ArbitratorDecision>;
  bump: number;
};

export type GhostProtectEscrowArgs = {
  /** Unique escrow ID */
  escrowId: number | bigint;
  /** Client (payer) */
  client: Address;
  /** Agent (service provider) */
  agent: Address;
  /** Payment amount */
  amount: number | bigint;
  /** Payment token mint */
  tokenMint: Address;
  /** Escrow status */
  status: EscrowStatusArgs;
  /** Job description (IPFS hash) */
  jobDescription: string;
  /** Delivery proof (IPFS hash) */
  deliveryProof: OptionOrNullable<string>;
  /** Deadline timestamp */
  deadline: number | bigint;
  /** Created timestamp */
  createdAt: number | bigint;
  /** Completed/disputed timestamp */
  completedAt: OptionOrNullable<number | bigint>;
  /** Dispute reason (if disputed) */
  disputeReason: OptionOrNullable<string>;
  /** Arbitrator decision (if disputed) */
  arbitratorDecision: OptionOrNullable<ArbitratorDecisionArgs>;
  bump: number;
};

/** Gets the encoder for {@link GhostProtectEscrowArgs} account data. */
export function getGhostProtectEscrowEncoder(): Encoder<GhostProtectEscrowArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["escrowId", getU64Encoder()],
      ["client", getAddressEncoder()],
      ["agent", getAddressEncoder()],
      ["amount", getU64Encoder()],
      ["tokenMint", getAddressEncoder()],
      ["status", getEscrowStatusEncoder()],
      [
        "jobDescription",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      [
        "deliveryProof",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      ["deadline", getI64Encoder()],
      ["createdAt", getI64Encoder()],
      ["completedAt", getOptionEncoder(getI64Encoder())],
      [
        "disputeReason",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      ["arbitratorDecision", getOptionEncoder(getArbitratorDecisionEncoder())],
      ["bump", getU8Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: GHOST_PROTECT_ESCROW_DISCRIMINATOR,
    }),
  );
}

/** Gets the decoder for {@link GhostProtectEscrow} account data. */
export function getGhostProtectEscrowDecoder(): Decoder<GhostProtectEscrow> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["escrowId", getU64Decoder()],
    ["client", getAddressDecoder()],
    ["agent", getAddressDecoder()],
    ["amount", getU64Decoder()],
    ["tokenMint", getAddressDecoder()],
    ["status", getEscrowStatusDecoder()],
    ["jobDescription", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    [
      "deliveryProof",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    ["deadline", getI64Decoder()],
    ["createdAt", getI64Decoder()],
    ["completedAt", getOptionDecoder(getI64Decoder())],
    [
      "disputeReason",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    ["arbitratorDecision", getOptionDecoder(getArbitratorDecisionDecoder())],
    ["bump", getU8Decoder()],
  ]);
}

/** Gets the codec for {@link GhostProtectEscrow} account data. */
export function getGhostProtectEscrowCodec(): Codec<
  GhostProtectEscrowArgs,
  GhostProtectEscrow
> {
  return combineCodec(
    getGhostProtectEscrowEncoder(),
    getGhostProtectEscrowDecoder(),
  );
}

export function decodeGhostProtectEscrow<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<GhostProtectEscrow, TAddress>;
export function decodeGhostProtectEscrow<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<GhostProtectEscrow, TAddress>;
export function decodeGhostProtectEscrow<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
):
  | Account<GhostProtectEscrow, TAddress>
  | MaybeAccount<GhostProtectEscrow, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getGhostProtectEscrowDecoder(),
  );
}

export async function fetchGhostProtectEscrow<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<GhostProtectEscrow, TAddress>> {
  const maybeAccount = await fetchMaybeGhostProtectEscrow(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeGhostProtectEscrow<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<GhostProtectEscrow, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeGhostProtectEscrow(maybeAccount);
}

export async function fetchAllGhostProtectEscrow(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<GhostProtectEscrow>[]> {
  const maybeAccounts = await fetchAllMaybeGhostProtectEscrow(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeGhostProtectEscrow(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<GhostProtectEscrow>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeGhostProtectEscrow(maybeAccount),
  );
}
